import { z } from "zod";

/**
 * Announcements spoken in the clinic: Vaulted posts { message, devices } to the clinic's Home Assistant
 * webhook, which says it on those Echo devices. Any time of day. First names only by default.
 */

export const ANNOUNCE_EVENTS = ["booking", "mobile_request", "plan_payment_failed"] as const;
export type AnnounceEvent = (typeof ANNOUNCE_EVENTS)[number];

export const ANNOUNCE_EVENT_INFO: Record<AnnounceEvent, { label: string; when: string; text: string; placeholders: string[] }> = {
  booking: {
    label: "New online booking",
    when: "A booking from the booking page or an event page is confirmed (after any card or deposit step), for any day",
    text: "New booking: {first_name}, {day} at {time}.",
    placeholders: ["first_name", "day", "time", "guests", "where"],
  },
  mobile_request: {
    label: "Mobile request",
    when: "A mobile visit request comes in",
    text: "New mobile request from {first_name} for {day}.",
    placeholders: ["first_name", "day", "time", "guests"],
  },
  plan_payment_failed: {
    label: "Membership payment failed",
    when: "A monthly membership charge is declined",
    text: "{first_name}'s {plan} payment didn't go through.",
    placeholders: ["first_name", "plan", "amount"],
  },
};

export const ANNOUNCE_PLACEHOLDERS: Record<string, string> = {
  first_name: "Client's first name",
  day: 'Visit day ("today", "tomorrow", or "Friday, October 30")',
  time: "Visit time (e.g. 2:30 PM)",
  guests: 'How many ("1 guest", "3 guests")',
  where: 'Where ("the clinic", or the event\'s name)',
  plan: "Membership name",
  amount: "Amount",
};

export interface AnnounceDevice {
  label: string;
  /** The device's name in Home Assistant's map (e.g. "clinic"). */
  key: string;
}

export interface AnnounceEventSetting {
  on: boolean;
  /** Device keys. */
  devices: string[];
  text: string;
}

export interface AnnounceView {
  /** Whether a webhook address is saved (never shown back). */
  hasWebhook: boolean;
  /** The saved address's host, to recognise it (e.g. "retsell.ddns.net:8123"). */
  webhookHost: string | null;
  devices: AnnounceDevice[];
  events: Record<AnnounceEvent, AnnounceEventSetting>;
  /** Latest announcements, newest first. */
  recent: { id: string; event: AnnounceEvent | "test"; message: string; devices: string[]; status: "queued" | "sent" | "failed"; error: string | null; at: string }[];
}

const deviceKey = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9_]{1,40}$/, "A device name in Home Assistant is lowercase letters, numbers, and _");

export const announceSetupSchema = z
  .object({
    /** A new webhook address; empty keeps the saved one. */
    webhook: z
      .string()
      .trim()
      .max(500)
      .default("")
      .refine((v) => !v || /^https:\/\/[^\s/]+\/api\/webhook\/[A-Za-z0-9_-]{8,}$/.test(v), "Paste the whole webhook address: https://…/api/webhook/…"),
    /** Remove the saved webhook. */
    clearWebhook: z.boolean().default(false),
    devices: z.array(z.object({ label: z.string().trim().min(1, "Name each device").max(60), key: deviceKey })).max(20),
    events: z.object(
      Object.fromEntries(
        ANNOUNCE_EVENTS.map((e) => [e, z.object({ on: z.boolean(), devices: z.array(deviceKey).max(20), text: z.string().trim().min(1, "Write what it says").max(300) })]),
      ) as Record<AnnounceEvent, z.ZodObject<{ on: z.ZodBoolean; devices: z.ZodArray<typeof deviceKey>; text: z.ZodString }>>,
    ),
  })
  .superRefine((v, ctx) => {
    const keys = v.devices.map((d) => d.key);
    if (new Set(keys).size !== keys.length) ctx.addIssue({ code: "custom", path: ["devices"], message: "Each device needs its own Home Assistant name" });
    for (const e of ANNOUNCE_EVENTS) {
      const s = v.events[e];
      if (s.devices.some((d) => !keys.includes(d))) ctx.addIssue({ code: "custom", path: ["events", e, "devices"], message: "Pick from the devices listed" });
      if (s.on && !s.devices.length) ctx.addIssue({ code: "custom", path: ["events", e, "devices"], message: `Pick where "${ANNOUNCE_EVENT_INFO[e].label}" is announced` });
    }
  });
export type AnnounceSetupInput = z.infer<typeof announceSetupSchema>;

export const announceTestSchema = z.object({ devices: z.array(deviceKey).min(1, "Pick a device").max(20) });
